import { inject, Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationService } from "@tylertech/core-module";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { ClassMetadata, KeyValuePair, PropertyMetadata } from "@tylertech/tyler-api-types";
import { MetadataIntermediary, MetadataKey } from "../../interfaces";
import
{
    CachedCodeLookupService,
    ResetStateService,
    MetadataInputSelectService,
    MetadataPropertyService,
    MetadataEventService
} from "../../services";
import { MetadataKeyHelpers } from "../../utilities";

@Injectable()
export class GridLookupService extends ResetStateService
{
    private $odataFilterFactory = inject(ODataFilterFactoryService);
    private $cachedCodeLookupService = inject(CachedCodeLookupService);
    private $metadataEventService = inject(MetadataEventService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataInputSelectService = inject(MetadataInputSelectService);

    private lookups: Record<string, MetadataKey[]> = {};

    private lookupCache: Record<string, KeyValuePair<MetadataKey, string>[]> = {};

    lookupsLoaded: Record<string, boolean> = {};

    propertiesForCacheClear: PropertyMetadata[] = [];

    constructor()
    {
        super(inject(NavigationService));

        this.$metadataEventService.onLookupTypeCacheClear.pipe(takeUntilDestroyed())
            .subscribe((type) =>
            {
                const typeMatch = this.propertiesForCacheClear.find(p => p.Lookup === type);

                let typeToCheck;
                if (type === "UserDefinedData" || type === "DocumentTypeName")
                    typeToCheck = type;
                else if (typeMatch)
                    typeToCheck = typeMatch.Lookup;

                if (typeToCheck)
                {
                    for (const key in this.lookupCache)
                    {
                        if (key.indexOf(typeToCheck) > -1)
                            this.lookupCache[key] = undefined;
                    }
                }
            });
    }

    loadLookups(metadata: ClassMetadata, rows: any[], filteredColumns: string[], includeLookups: string[], entities: any[]): Promise<any[]>
    {
        const self = this;
        const promises = [];

        this.propertiesForCacheClear = this.buildPropertiesForCacheClear(metadata, filteredColumns, includeLookups, entities);

        this.propertiesForCacheClear.forEach(p =>
        {
            if (this.lookups[p.Name] && this.lookups[p.Name].length > 0 && !filteredColumns)
                self.lookupsLoaded[p.Lookup] = false;
            const promise = self.lookupValuesForTable(p, rows);
            promises.push(promise);
        });
        return Promise.all(promises)
            .then(_r =>
            {
                return rows;
            });
    }

    LookupValueForTable(
        property: PropertyMetadata,
        rows: any[],
        ids: string[]
    ): Promise<void>
    {
        this.lookups[property.Name] = [];
        ids.forEach(id => this.lookups[property.Name].push(id));
        return this.lookupValuesForTable(property, rows);
    }

    lookupValuesForTable(property: PropertyMetadata, rows: any[], pagedLookup?: any[]): Promise<void>
    {
        const self = this;
        const filter = this.buildExistingFilter(property);
        // add something to this.$metadataInputSelectService that updates a property
        // what we really want is metadata-input-typeahead showSpinner to be true
        return this.codeLookupGet(property.Lookup, pagedLookup ? pagedLookup : this.lookups[property.Name], filter)
            .then(
                (results: KeyValuePair<MetadataKey, string>[]) =>
                {
                    if (!self.lookupCache[property.Lookup + '|' + filter]) self.lookupCache[property.Lookup + '|' + filter] = [];
                    results = self.$metadataInputSelectService.MergeSelectOptions(property.Lookup, results, property);
                    results = self.mergeFreeEntryOptions(results, property);
                    if (!Utilities.IsNullOrUndefined(results))
                    {
                        results.forEach(r =>
                        {
                            if (self.lookupCache[property.Lookup + '|' + filter].indexOf(r) === -1)
                            {
                                self.lookupCache[property.Lookup + '|' + filter].push(r);
                            }
                        });
                    }
                    rows.forEach(row =>
                    {
                        const value = row[property.Name];
                        const result: KeyValuePair<MetadataKey, string>[] = [];
                        if (!Utilities.IsNullOrUndefined(results) || self.lookupCache[property.Lookup + '|' + filter].length > 0)
                        {
                            if (property.InputType === "MultiSelect")
                            {
                                const values = value?.toString()
                                    .split(",");
                                values.forEach(value =>
                                {
                                    const toPush = self.lookupCache[property.Lookup + '|' + filter].find(
                                        x =>
                                            x.Key.toString() === value.toString() || x.Value.toString() === value.toString()
                                    );
                                    if (toPush) result.push(toPush);
                                });
                            }
                            else
                            {
                                const toPush = self.lookupCache[property.Lookup + '|' + filter].find(
                                    x => x.Key.toString() === value?.toString() || x.Value?.toString() === value?.toString()
                                );
                                if (toPush) result.push(toPush);
                            }
                        }
                        const val = result.length > 0 ? result.map(x => x.Value).join(",") : "";
                        if (!property.LookupProperty || !Utilities.IsNullOrUndefinedOrEmpty(val))
                            row[property.Name] = val;
                    });
                    if (self.lookups[property.Name])
                    {
                        self.fireLookupLoadedListeners(property.Lookup);
                        self.lookupsLoaded[property.Lookup] = true;
                    }
                },
                () =>
                    rows.forEach(row =>
                    {
                        row[property.Name] = "Error Loading";
                    })
            );
    }

    private codeLookupGet(
        lookup: string,
        ids: MetadataKey[],
        existingFilter: string
    ): Promise<KeyValuePair<MetadataKey, string>[]>
    {
        const newIds = [];
        const self = this;
        if (!Utilities.IsNullOrUndefined(ids))
        {
            ids.filter(i => !this.isKeyEmpty(i))
                .forEach(id =>
                {
                    if (!self.lookupCache[lookup + '|' + existingFilter] || self.lookupCache[lookup + '|' + existingFilter].filter(k => k.Key === id).length === 0)
                    {
                        newIds.push(id);
                    }
                });
        }
        if (!Utilities.IsNullOrUndefined(newIds) && newIds.length > 0)
        {
            let batchLength = 100;
            if (newIds[0].toString().length > 10)
                batchLength = 40;
            const loaders: Promise<any>[] = [];
            for (let i = 0; i < newIds.length; i = i + batchLength)
            {
                const builder = this.$odataFilterFactory
                    .createBuilder()
                    .in("Key", newIds.slice(i, Math.min(i + batchLength, newIds.length)));
                if (!Utilities.IsNullOrUndefinedOrEmpty(existingFilter)) builder.constantFilter(existingFilter);
                loaders.push(this.$cachedCodeLookupService.Get(lookup, builder.build()));
            }
            return Promise.all(loaders)
                .then(results =>
                {
                    return Array.prototype.concat.apply([], results);
                });
        }
        return Promise.resolve(self.lookupCache[lookup + '|' + existingFilter]);
    }

    private buildPropertiesForCacheClear(metadata: ClassMetadata, filteredColumns: string[], includeLookups: string[], entities: any[]): PropertyMetadata[]
    {
        const properties: PropertyMetadata[] = [];
        metadata.Properties.forEach(x =>
        {
            if (x.ClassMetadata)
                properties.push(...this.buildPropertiesForCacheClear(x.ClassMetadata, filteredColumns, includeLookups, entities.map(e => e && e[x.Name])));
            else
            {
                if (!Utilities.IsNullOrUndefinedOrEmpty(x.Lookup))
                {
                    const inFilter = entities.some(entity => entity && !this.$metadataPropertyService.isHidden(x, entity, entity.$$Parent));
                    let include = false;
                    if (filteredColumns)
                    {
                        filteredColumns = filteredColumns.map(x => x.substring(x.lastIndexOf(".") + 1));
                        include = inFilter && filteredColumns.indexOf(x.Name) > -1;
                    }
                    else if (!Utilities.IsNullOrUndefined(includeLookups) && includeLookups.indexOf(x.Name) < 0)
                        include = false;
                    else
                        include = inFilter;

                    if (include)
                        properties.push(x);
                }
            }
        });
        return properties;
    }


    private mergeFreeEntryOptions(
        results: KeyValuePair<MetadataKey, string>[],
        propertyMetadata: PropertyMetadata
    ): KeyValuePair<MetadataKey, string>[]
    {
        if (!propertyMetadata.FreeEntry || Utilities.IsNullOrUndefined(this.lookups[propertyMetadata.Name]))
            return results;

        this.lookups[propertyMetadata.Name].forEach(x =>
        {
            const kvp = <KeyValuePair<MetadataKey, string>>{
                Key: x,
                Value: x
            };
            if (x !== 0 && x !== "" && results.indexOf(kvp) < 0)
                results.push(kvp);
        });
        return results;
    }

    setPropertyIds(model: any, intermediaries: MetadataIntermediary[], metadata: ClassMetadata, includeLookups: string[]): void
    {
        const self = this;
        metadata.Properties.forEach(prop =>
        {
            if (!Utilities.IsNullOrUndefined(includeLookups) && includeLookups.indexOf(prop.Name) < 0)
                return;
            if (
                !Utilities.IsNullOrUndefined(model) &&
                !Utilities.IsNullOrUndefinedOrEmpty(prop.Lookup) &&
                !prop.Hidden &&
                !Utilities.IsNullOrUndefined(intermediaries.find(x => x.name === prop.Name))
            )
            {
                if (!Utilities.IsNullOrUndefined(model[prop.Name]))
                {
                    const ids = Array.isArray(<MetadataKey>model[prop.Name])
                        ? <MetadataKey[]>model[prop.Name]
                        : [<MetadataKey>model[prop.Name]];
                    if (Utilities.IsNullOrUndefined(self.lookups[prop.Name])) self.lookups[prop.Name] = [];
                    ids.forEach(id =>
                    {
                        if (this.lookups[prop.Name].indexOf(id) < 0) this.lookups[prop.Name].push(id);
                    });
                }
            }
            else if (!Utilities.IsNullOrUndefined(prop.ClassMetadata))
            {
                self.setPropertyIds(model[prop.Name], intermediaries, prop.ClassMetadata, includeLookups);
            }
        });
    }

    private buildExistingFilter(property: PropertyMetadata): string
    {
        if (!Utilities.IsNullOrUndefined(property?.ConstantFilter) && !property.ConstantFilter.match("[{}]"))
            return `(${property.ConstantFilter})`;
    }

    private isKeyEmpty(id: MetadataKey): boolean
    {
        return MetadataKeyHelpers.isNullKey(id) || (MetadataKeyHelpers.isStringKey(id) && id === "");
    }

    private readonly lookupLoaded$ = new Subject<string>();

    get onLookupLoaded(): Observable<string>
    {
        return this.lookupLoaded$.asObservable();
    }

    fireLookupLoadedListeners(lookup: string): void
    {
        this.lookupLoaded$.next(lookup);
    }

}
